// ---------------------------------------------------------------------------
// reviewSlugs.js
// Builds review-intent HTML file names:
//
//   [keyword-slug]-[review-phrase]-[6 random chars].html
//   e.g. seo-neo-reviews-complaints-6gsua5.html
//
// The review phrases match what people search for before buying (reviews,
// complaints, legit or scam, does it work...). Every page gets a different
// phrase until the list runs out, then phrases repeat with new random codes.
// File names are never duplicated within a batch.
// ---------------------------------------------------------------------------
import { slugify } from './random.js';

const YEAR = new Date().getFullYear();

export const REVIEW_PHRASES = [
  // Reviews & complaints
  'reviews-complaints',
  'reviews-and-complaints',
  'customer-complaints',
  `complaints-${YEAR}`,
  'reviews-consumer-reports',
  'consumer-reports',
  // Honest / real reviews
  'honest-review',
  'honest-reviews',
  'my-honest-review',
  'honest-opinion',
  'real-reviews',
  'real-user-reviews',
  'customer-reviews',
  'user-reviews',
  'unbiased-review',
  'independent-review',
  'expert-review',
  // Legit or scam
  'reviews-legit-or-scam',
  'legit-or-scam',
  'scam-or-legit',
  'is-it-legit',
  'is-it-a-scam',
  'scam-alert',
  'buyer-beware',
  'reviews-red-flags',
  'review-warning',
  // Truth / exposed
  'reviews-exposed',
  'exposed-review',
  'truth-revealed',
  'the-truth',
  // Does it work / results
  'does-it-work',
  'does-it-really-work',
  'reviews-results',
  'real-results',
  'results-review',
  // Worth it / before you buy
  'worth-it-review',
  'is-it-worth-it',
  'review-before-you-buy',
  'watch-before-you-buy',
  'reviews-what-to-know',
  'reviews-pros-and-cons',
  'pros-and-cons',
  // In-depth / video
  'full-review',
  'in-depth-review',
  'detailed-review',
  'hands-on-review',
  'first-impressions',
  'my-experience',
  'video-review',
  'review-video',
  // Price / official
  'reviews-pricing',
  'price-and-reviews',
  'reviews-cost',
  'reviews-discount',
  'reviews-official-site',
  'official-reviews',
  'reviews-refund-policy',
  // Feedback / ratings
  'reviews-feedback',
  'testimonials',
  'reviews-and-testimonials',
  'reviews-ratings',
  'rating-and-review',
  // Year
  `review-${YEAR}`,
  `reviews-${YEAR}`,
  `review-update-${YEAR}`,
];

const LOWER_ALNUM = 'abcdefghijklmnopqrstuvwxyz0123456789'.split('');

/** Shuffle a copy of an array with the seeded RNG (Fisher-Yates). */
const shuffled = (items, rng) => {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng._next() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

/**
 * Build `count` unique review-style file names.
 * @param {string} keyword - Short keyword, e.g. "SEO NEO" -> "seo-neo"
 * @param {number} count
 * @param {SeededRNG} rng
 * @returns {string[]}
 */
export const buildReviewFilenames = (keyword, count, rng) => {
  const base = slugify(keyword || 'product') || 'product';
  const names = [];
  const used = new Set();
  let order = [];
  while (names.length < count) {
    if (!order.length) order = shuffled(REVIEW_PHRASES, rng);
    const phrase = order.pop();
    const name = `${base}-${phrase}-${rng.choices(LOWER_ALNUM, 6).join('')}.html`;
    if (used.has(name)) continue;
    used.add(name);
    names.push(name);
  }
  return names;
};
